import { setContext, getContext } from "svelte";
import type { FloorPlan } from "#backend/modules/rental-listings/rental-listings.schemas.js";

type RentalTemplateBaseConfig = {
	name: string;
	tagline: string;
	logo: string;
	address: {
		street: string;
		city: string;
		state: string;
		zip: string;
	};
	geo: {
		latitude: string;
		longitude: string;
	};
	amenities: string[];
	priceRange: string;
	metaDescription: string;
	metaKeywords: string;
	tenantPortal: string;
	contact: {
		phone: string;
		email: string;
	};
	events?: {
		date: string;
		title: string;
		description: string;
	}[];
	eventImage: string;
	websiteUrl: string;
	socialMedia: {
		facebook: string;
		instagram: string;
	};
	heroImage: string;
	heroVideoPublicId?: string;
	apartmentFeatures: string[];
	communityFeatures: string[];
	detailsImage: string;
	neighborhood: string[];
	floorPlans: FloorPlan[];
	propertyImages: {
		url: string;
		alt: string;
	}[];
	welcomeTagline: string;
	welcomeDescription: string;
	floatingBanner?: {
		title: string;
		subTitle: string;
	};
	integrations?: {
		knock?: {
			propertyId: string;
			communityId: string;
			embedding?: {
				page?: string;
				container?: string;
				showChatbot?: boolean;
			};
		};
	};
	overrides?: {
		residentPortalUrl?: string;
		applyLinkUrl?: string;
	};
};

type RentalTemplateAvailabilityConfig =
	| { buildiumPropertyIds: readonly [string, ...string[]] }
	| { buildiumPropertyIds: readonly []; availabilityUrl?: string };

export type RentalTemplateContextConfig = RentalTemplateBaseConfig &
	RentalTemplateAvailabilityConfig;

export class RentalTemplateContext {
	name = $state<RentalTemplateContextConfig["name"]>("");
	tagline = $state<RentalTemplateContextConfig["tagline"]>("");
	logo = $state<RentalTemplateContextConfig["logo"]>("");
	websiteUrl = $state<RentalTemplateContextConfig["websiteUrl"]>("");
	address = $state<RentalTemplateContextConfig["address"]>({
		street: "",
		city: "",
		state: "",
		zip: ""
	});
	geo = $state<RentalTemplateContextConfig["geo"]>({
		latitude: "",
		longitude: ""
	});
	contact = $state<RentalTemplateContextConfig["contact"]>({
		phone: "",
		email: ""
	});
	tenantPortal = $state<RentalTemplateContextConfig["tenantPortal"]>("");
	welcomeTagline = $state<RentalTemplateContextConfig["welcomeTagline"]>("");
	welcomeDescription = $state<RentalTemplateContextConfig["welcomeDescription"]>("");
	amenities = $state<RentalTemplateContextConfig["amenities"]>([]);
	apartmentFeatures = $state<RentalTemplateContextConfig["apartmentFeatures"]>([]);
	communityFeatures = $state<RentalTemplateContextConfig["communityFeatures"]>([]);
	priceRange = $state<RentalTemplateContextConfig["priceRange"]>("");
	heroImage = $state<RentalTemplateContextConfig["heroImage"]>("");
	heroVideoPublicId = $state<RentalTemplateContextConfig["heroVideoPublicId"]>("");
	propertyImages = $state<RentalTemplateContextConfig["propertyImages"]>([]);
	floorPlans = $state<RentalTemplateContextConfig["floorPlans"]>([]);
	eventImage = $state<RentalTemplateContextConfig["eventImage"]>("");
	detailsImage = $state<RentalTemplateContextConfig["detailsImage"]>("");
	floatingBanner = $state<RentalTemplateContextConfig["floatingBanner"] | null>(null);
	metaDescription = $state<RentalTemplateContextConfig["metaDescription"]>("");
	metaKeywords = $state<RentalTemplateContextConfig["metaKeywords"]>("");
	socialMedia = $state<RentalTemplateContextConfig["socialMedia"]>({
		facebook: "",
		instagram: ""
	});
	neighborhood = $state<RentalTemplateContextConfig["neighborhood"]>([]);
	buildiumPropertyIds = $state<readonly string[]>([]);
	availabilityUrl = $state<string | null>(null);
	events = $state<RentalTemplateContextConfig["events"] | null>(null);
	integrations = $state<RentalTemplateContextConfig["integrations"] | null>(null);
	overrides = $state<RentalTemplateContextConfig["overrides"] | null>(null);

	constructor(data: RentalTemplateContextConfig) {
		this.name = data.name;
		this.tagline = data.tagline;
		this.logo = data.logo;
		this.websiteUrl = data.websiteUrl;
		this.address = data.address;
		this.geo = data.geo;
		this.contact = data.contact;
		this.socialMedia = data.socialMedia;
		this.floatingBanner = data.floatingBanner ?? null;
		this.amenities = data.amenities;
		this.priceRange = data.priceRange;
		this.metaDescription = data.metaDescription;
		this.metaKeywords = data.metaKeywords;
		this.buildiumPropertyIds = data.buildiumPropertyIds;
		this.availabilityUrl = ("availabilityUrl" in data ? data.availabilityUrl : null) ?? null;
		this.tenantPortal = data.tenantPortal;
		this.events = data.events;
		this.eventImage = data.eventImage;
		this.heroImage = data.heroImage;
		this.heroVideoPublicId = data.heroVideoPublicId;
		this.apartmentFeatures = data.apartmentFeatures;
		this.communityFeatures = data.communityFeatures;
		this.detailsImage = data.detailsImage;
		this.neighborhood = data.neighborhood;
		this.floorPlans = data.floorPlans;
		this.propertyImages = data.propertyImages;
		this.welcomeTagline = data.welcomeTagline;
		this.welcomeDescription = data.welcomeDescription;
		this.integrations = data.integrations ?? null;
		this.overrides = data.overrides ?? null;
	}
}

const RENTAL_TEMPLATE_CONTEXT_KEY = "RentalTemplateContext";

export function setRentalTemplateContext(data: RentalTemplateContextConfig): RentalTemplateContext {
	return setContext(RENTAL_TEMPLATE_CONTEXT_KEY, new RentalTemplateContext(data));
}

export function getRentalTemplateContext(): RentalTemplateContext | undefined {
	return getContext<RentalTemplateContext>(RENTAL_TEMPLATE_CONTEXT_KEY);
}
